/**
 * Wren + Glory — Custom Jacket Customization
 * Vanilla JS, no jQuery.
 */

/* ========================================================================
   CloudinaryUploader
   ======================================================================== */

class CloudinaryUploader {
  constructor({ cloudName, preset, maxSizeMB, allowedTypes }) {
    this.cloudName = cloudName;
    this.preset = preset;
    this.maxSizeMB = maxSizeMB;
    this.allowedTypes = allowedTypes
      .split(',')
      .map(function (t) { return t.trim().toLowerCase(); });
  }

  validate(file) {
    if (!file) {
      return { valid: false, error: 'No file selected.' };
    }

    var fileType = file.type.toLowerCase();

    // HEIC files from iPhones may have empty or generic type
    var isHEIC = /\.heic$/i.test(file.name);
    if (!isHEIC && this.allowedTypes.length > 0 && this.allowedTypes.indexOf(fileType) === -1) {
      return {
        valid: false,
        error: 'File type not supported. Please use JPG, PNG, or WebP.'
      };
    }

    var maxBytes = this.maxSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      return {
        valid: false,
        error: 'File too large. Maximum size is ' + this.maxSizeMB + 'MB.'
      };
    }

    return { valid: true, error: '' };
  }

  upload(file, onProgress) {
    var self = this;
    return new Promise(function (resolve, reject) {
      var url = 'https://api.cloudinary.com/v1_1/' + self.cloudName + '/image/upload';
      var formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', self.preset);

      var xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);

      xhr.upload.addEventListener('progress', function (e) {
        if (e.lengthComputable && typeof onProgress === 'function') {
          var percent = Math.round((e.loaded / e.total) * 100);
          onProgress(percent);
        }
      });

      xhr.addEventListener('load', function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            var data = JSON.parse(xhr.responseText);
            resolve({ url: data.secure_url, publicId: data.public_id });
          } catch (e) {
            reject(new Error('Failed to parse upload response.'));
          }
        } else {
          reject(new Error('Upload failed with status ' + xhr.status));
        }
      });

      xhr.addEventListener('error', function () {
        reject(new Error('Upload failed. Please check your connection and try again.'));
      });

      xhr.send(formData);
    });
  }
}

/* ========================================================================
   UploadZone
   ======================================================================== */

class UploadZone {
  constructor(element, uploader) {
    this.el = element;
    this.uploader = uploader;
    this.uploading = false;

    this.droparea = this.el.querySelector('[data-droparea]');
    this.fileInput = this.el.querySelector('[data-file-input]');
    this.preview = this.el.querySelector('[data-preview]');
    this.previewImage = this.el.querySelector('[data-preview-image]');
    this.removeBtn = this.el.querySelector('[data-preview-remove]');
    this.progress = this.el.querySelector('[data-progress]');
    this.progressBar = this.el.querySelector('[data-progress-bar]');
    this.errorEl = this.el.querySelector('[data-error]');
    this.hiddenInput = this.el.querySelector('[data-image-input]');
    this.textarea = this.el.querySelector('textarea');

    this.zoneKey = this.el.getAttribute('data-zone-key');
    this.required = this.el.getAttribute('data-required') === 'true';

    this._bindEvents();
  }

  _bindEvents() {
    var self = this;

    // Click to browse
    this.droparea.addEventListener('click', function () {
      self.fileInput.click();
    });

    // Keyboard: Enter/Space to open file picker
    this.droparea.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        self.fileInput.click();
      }
    });

    // File input change
    this.fileInput.addEventListener('change', function () {
      if (self.fileInput.files && self.fileInput.files[0]) {
        self._handleFile(self.fileInput.files[0]);
      }
    });

    // Drag and drop
    this.droparea.addEventListener('dragenter', function (e) {
      e.preventDefault();
      e.stopPropagation();
      self.droparea.classList.add('wg-upload-zone__droparea--dragover');
    });

    this.droparea.addEventListener('dragover', function (e) {
      e.preventDefault();
      e.stopPropagation();
      self.droparea.classList.add('wg-upload-zone__droparea--dragover');
    });

    this.droparea.addEventListener('dragleave', function (e) {
      e.preventDefault();
      e.stopPropagation();
      self.droparea.classList.remove('wg-upload-zone__droparea--dragover');
    });

    this.droparea.addEventListener('drop', function (e) {
      e.preventDefault();
      e.stopPropagation();
      self.droparea.classList.remove('wg-upload-zone__droparea--dragover');
      if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]) {
        self._handleFile(e.dataTransfer.files[0]);
      }
    });

    // Paste
    this.el.addEventListener('paste', function (e) {
      var items = e.clipboardData && e.clipboardData.items;
      if (!items) return;
      for (var i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          var file = items[i].getAsFile();
          if (file) {
            e.preventDefault();
            self._handleFile(file);
            break;
          }
        }
      }
    });

    // Remove button
    this.removeBtn.addEventListener('click', function () {
      self._reset();
    });
  }

  _handleFile(file) {
    var self = this;
    this._clearError();

    var validation = this.uploader.validate(file);
    if (!validation.valid) {
      this._showError(validation.error);
      return;
    }

    this.uploading = true;
    this._showProgress(0);
    this.droparea.classList.add('wg-upload-zone__droparea--has-file');

    this.uploader.upload(file, function (percent) {
      self._showProgress(percent);
    }).then(function (result) {
      self.uploading = false;
      self._hideProgress();
      self.hiddenInput.value = result.url;
      self._showPreview(result.url);
    }).catch(function (err) {
      self.uploading = false;
      self._hideProgress();
      self.droparea.classList.remove('wg-upload-zone__droparea--has-file');
      self._showError(err.message || 'Upload failed. Please try again.');
    });
  }

  _showProgress(percent) {
    this.progress.classList.add('wg-upload-zone__progress--visible');
    this.progressBar.style.width = percent + '%';
    this.progress.setAttribute('aria-valuenow', percent);
  }

  _hideProgress() {
    this.progress.classList.remove('wg-upload-zone__progress--visible');
    this.progressBar.style.width = '0%';
    this.progress.setAttribute('aria-valuenow', '0');
  }

  _showPreview(url) {
    this.previewImage.src = url;
    this.preview.classList.add('wg-upload-zone__preview--visible');
  }

  _showError(message) {
    var self = this;
    this.errorEl.textContent = message;
    this._errorTimeout = setTimeout(function () {
      self._clearError();
    }, 5000);
  }

  _clearError() {
    if (this._errorTimeout) {
      clearTimeout(this._errorTimeout);
    }
    this.errorEl.textContent = '';
  }

  _reset() {
    this.hiddenInput.value = '';
    this.previewImage.src = '';
    this.preview.classList.remove('wg-upload-zone__preview--visible');
    this.droparea.classList.remove('wg-upload-zone__droparea--has-file');
    this.fileInput.value = '';
    this._hideProgress();
    this._clearError();
  }

  hasContent() {
    return (this.hiddenInput.value !== '') || (this.textarea.value.trim() !== '');
  }

  isUploading() {
    return this.uploading;
  }
}

/* ========================================================================
   CustomizeForm
   ======================================================================== */

class CustomizeForm {
  constructor(sectionEl) {
    this.section = sectionEl;
    this.currentStep = 3;

    // Config from data attributes
    var cloudName = this.section.getAttribute('data-cloud-name');
    var preset = this.section.getAttribute('data-preset');
    var maxSize = parseInt(this.section.getAttribute('data-max-size'), 10) || 10;
    var allowedTypes = this.section.getAttribute('data-allowed-types') || '';
    this.isSendYourOwn = this.section.getAttribute('data-is-send-your-own') === 'true';

    // Uploader
    this.uploader = new CloudinaryUploader({
      cloudName: cloudName,
      preset: preset,
      maxSizeMB: maxSize,
      allowedTypes: allowedTypes
    });

    // Upload zones
    this.zones = [];
    var zoneEls = this.section.querySelectorAll('[data-upload-zone]');
    for (var i = 0; i < zoneEls.length; i++) {
      this.zones.push(new UploadZone(zoneEls[i], this.uploader));
    }

    // Panels
    this.stepPanels = this.section.querySelectorAll('[data-step-panel]');
    this.stepIndicator = this.section.querySelector('[data-step-indicator]');

    // Buttons
    this.nextBtn = this.section.querySelector('[data-next-step]');
    this.prevBtn = this.section.querySelector('[data-prev-step]');
    this.submitBtn = this.section.querySelector('[data-submit-btn]');

    // Variant elements
    this.variantIdInput = this.section.querySelector('[data-variant-id-input]');
    this.priceEl = this.section.querySelector('[data-price]');
    this.availabilityEl = this.section.querySelector('[data-availability]');

    // Form
    this.form = this.section.querySelector('[data-customize-form]');

    // Variant data
    this.variants = [];
    var variantJsonEl = this.section.querySelector('[data-variant-json]');
    if (variantJsonEl) {
      try {
        this.variants = JSON.parse(variantJsonEl.textContent);
      } catch (e) {
        // Variant data unavailable
      }
    }

    this._bindEvents();
  }

  _bindEvents() {
    var self = this;

    // Next step
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () {
        self._goToStep(4);
      });
    }

    // Previous step
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () {
        self._goToStep(3);
      });
    }

    // Variant option selection (grouped Color / Size selectors)
    this.optionInputs = this.section.querySelectorAll('[data-option-group] input[type="radio"]');
    for (var i = 0; i < this.optionInputs.length; i++) {
      this.optionInputs[i].addEventListener('change', function () {
        self._onOptionChange();
      });
    }
    // Sync price / availability with the default selection on load.
    if (this.optionInputs.length > 0) {
      this._onOptionChange();
    }

    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', function (e) {
        if (!self._validateSubmit()) {
          e.preventDefault();
          return false;
        }
      });
    }
  }

  _goToStep(step) {
    this.currentStep = step;

    // Toggle panel visibility
    for (var i = 0; i < this.stepPanels.length; i++) {
      var panel = this.stepPanels[i];
      var panelStep = parseInt(panel.getAttribute('data-step-panel'), 10);
      if (panelStep === step) {
        panel.classList.add('wg-customize__step-panel--active');
      } else {
        panel.classList.remove('wg-customize__step-panel--active');
      }
    }

    // Update step indicator
    this._updateStepIndicator(step);

    // Scroll to section top
    this.section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  _updateStepIndicator(currentStep) {
    if (!this.stepIndicator) return;
    var dots = this.stepIndicator.querySelectorAll('.wg-customize__step-dot');
    for (var i = 0; i < dots.length; i++) {
      var dotStep = parseInt(dots[i].getAttribute('data-step'), 10);
      dots[i].classList.remove('wg-customize__step-dot--active', 'wg-customize__step-dot--completed');
      dots[i].removeAttribute('aria-current');
      if (dotStep < currentStep) {
        dots[i].classList.add('wg-customize__step-dot--completed');
      } else if (dotStep === currentStep) {
        dots[i].classList.add('wg-customize__step-dot--active');
        dots[i].setAttribute('aria-current', 'step');
      }
    }
  }

  _getSelectedOptions() {
    // Returns selected values keyed by option position (1-based) → array index = position - 1.
    var selected = [];
    for (var i = 0; i < this.optionInputs.length; i++) {
      var input = this.optionInputs[i];
      if (input.checked) {
        var pos = parseInt(input.getAttribute('data-option-position'), 10);
        selected[pos - 1] = input.value;
      }
    }
    return selected;
  }

  _findVariant(selectedOptions) {
    for (var i = 0; i < this.variants.length; i++) {
      var opts = this.variants[i].options || [];
      var match = true;
      for (var j = 0; j < selectedOptions.length; j++) {
        if (String(opts[j]) !== String(selectedOptions[j])) {
          match = false;
          break;
        }
      }
      if (match) return this.variants[i];
    }
    return null;
  }

  _onOptionChange() {
    var selected = this._getSelectedOptions();
    this._updateOptionAvailability(selected);

    var variant = this._findVariant(selected);

    // No matching variant for this combination (e.g. that pairing doesn't exist).
    if (!variant) {
      if (this.priceEl) this.priceEl.textContent = '';
      if (this.availabilityEl) {
        this.availabilityEl.innerHTML =
          '<span class="wg-customize__availability--unavailable">Unavailable</span>';
      }
      if (this.nextBtn) this.nextBtn.disabled = true;
      return;
    }

    this._onVariantChange(variant.id);
  }

  _updateOptionAvailability(selectedOptions) {
    // Mark each option value that, combined with the other current selections,
    // has no available variant — so shoppers see which pairings are sold out.
    for (var i = 0; i < this.optionInputs.length; i++) {
      var input = this.optionInputs[i];
      var pos = parseInt(input.getAttribute('data-option-position'), 10);
      var candidate = selectedOptions.slice();
      candidate[pos - 1] = input.value;

      var available = false;
      for (var v = 0; v < this.variants.length; v++) {
        var opts = this.variants[v].options || [];
        var match = true;
        for (var j = 0; j < candidate.length; j++) {
          if (candidate[j] != null && String(opts[j]) !== String(candidate[j])) {
            match = false;
            break;
          }
        }
        if (match && this.variants[v].available) {
          available = true;
          break;
        }
      }

      var label = this.section.querySelector('label[for="' + input.id + '"]');
      if (label) {
        if (available) {
          label.classList.remove('wg-customize__option-value--unavailable');
        } else {
          label.classList.add('wg-customize__option-value--unavailable');
        }
      }
    }
  }

  _onVariantChange(variantId) {
    var variant = null;
    for (var i = 0; i < this.variants.length; i++) {
      if (String(this.variants[i].id) === String(variantId)) {
        variant = this.variants[i];
        break;
      }
    }

    if (!variant) return;

    // Update hidden variant ID input
    if (this.variantIdInput) {
      this.variantIdInput.value = variant.id;
    }

    // Update price display
    if (this.priceEl) {
      this.priceEl.textContent = this._formatMoney(variant.price);
    }

    // Update availability
    if (this.availabilityEl) {
      if (variant.available) {
        this.availabilityEl.innerHTML = '';
      } else {
        this.availabilityEl.innerHTML = '<span class="wg-customize__availability--unavailable">Sold out</span>';
      }
    }

    // Enable/disable next button
    if (this.nextBtn) {
      this.nextBtn.disabled = !variant.available;
    }

    // Dispatch Stiletto custom event
    document.dispatchEvent(new CustomEvent('product:variant-change', {
      detail: { variant: variant }
    }));
  }

  _formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    return '$' + amount;
  }

  _validateSubmit() {
    // Check for active uploads
    for (var i = 0; i < this.zones.length; i++) {
      if (this.zones[i].isUploading()) {
        this._showSubmitError('Please wait for uploads to complete.');
        return false;
      }
    }

    // Check required zones
    for (var j = 0; j < this.zones.length; j++) {
      if (this.zones[j].required && !this.zones[j].hasContent()) {
        this._showSubmitError('Please complete all required zones before submitting.');
        var zoneEl = this.zones[j].el;
        zoneEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }
    }

    // Prevent double-submit
    if (this.submitBtn) {
      this.submitBtn.disabled = true;
      this.submitBtn.classList.add('wg-customize__submit-btn--loading');
    }

    return true;
  }

  _showSubmitError(message) {
    // Find or create a general error area
    var existingError = this.section.querySelector('[data-form-error]');
    if (!existingError) {
      existingError = document.createElement('div');
      existingError.setAttribute('data-form-error', '');
      existingError.setAttribute('role', 'alert');
      existingError.style.color = 'var(--color-products-stock-bad, #cc0000)';
      existingError.style.marginBottom = 'var(--spacing-4, 16px)';
      existingError.style.fontWeight = '700';
      if (this.submitBtn) {
        this.submitBtn.parentNode.insertBefore(existingError, this.submitBtn);
      }
    }
    existingError.textContent = message;

    setTimeout(function () {
      existingError.textContent = '';
    }, 5000);
  }
}

/* ========================================================================
   Initialization
   ======================================================================== */

document.addEventListener('DOMContentLoaded', function () {
  var sections = document.querySelectorAll('[data-section-type="wg-customize-form"]');
  for (var i = 0; i < sections.length; i++) {
    new CustomizeForm(sections[i]);
  }
});
